import { useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { useUser } from "@/app/session.tsx";
import { describeCitation } from "@/components/Answer.tsx";
import { V2AnswerBody, V2FootNote } from "@/components/AnswerV2.tsx";
import { Button, Panel } from "@/components/primitives.tsx";
import { TopBar } from "@/components/TopBar.tsx";
import { api, ApiError, type PlaygroundResponse } from "@/lib/api.ts";
import { citationKey, type Citation } from "@/lib/api-types.ts";
import {
  placeV2Claims,
  readV2Claims,
  readV2Faithfulness,
  V2_SCHEMA,
} from "@/lib/queryV2.ts";

export type PlaygroundErrorKey =
  | "playground.errors.validation"
  | "playground.errors.authentication"
  | "playground.errors.forbidden"
  | "playground.errors.sources"
  | "playground.errors.tooLarge"
  | "playground.errors.rateLimit"
  | "playground.errors.backend"
  | "playground.errors.unknown";

/** HTTP details are useful to developers but not actionable to an administrator. Keep
 * this vocabulary small and stable, including when FastAPI returns field-level 422s. */
export function playgroundErrorKey(error: unknown): PlaygroundErrorKey {
  if (!(error instanceof ApiError)) return "playground.errors.unknown";
  if (error.status === 400 || error.status === 409 || error.status === 422) {
    return "playground.errors.validation";
  }
  if (error.status === 401) return "playground.errors.authentication";
  if (error.status === 403) return "playground.errors.forbidden";
  if (error.status === 404) return "playground.errors.sources";
  if (error.status === 413) return "playground.errors.tooLarge";
  if (error.status === 429) return "playground.errors.rateLimit";
  if (error.status === 502 || error.status === 503) return "playground.errors.backend";
  return "playground.errors.unknown";
}

function isRenderableCitation(value: Citation): boolean {
  if (!value || typeof value !== "object") return false;
  if (value.kind === "chunk") {
    return typeof value.document_title === "string" && Array.isArray(value.section_path);
  }
  if (value.kind === "tool") {
    return typeof value.document_title === "string";
  }
  return false;
}

export function PlaygroundResult({ result }: { result: PlaygroundResponse }) {
  const { t, i18n } = useTranslation();
  const citations = useMemo(
    () => result.citations.filter(isRenderableCitation),
    [result.citations],
  );
  const evidenceIndex = useMemo(
    () => new Map(citations.map((citation, index) => [citationKey(citation), index + 1])),
    [citations],
  );
  const annotation = useMemo(
    () => ({
      claims: readV2Claims(result.claims),
      faithfulness: readV2Faithfulness(result.faithfulness),
    }),
    [result.claims, result.faithfulness],
  );
  const placement = useMemo(
    () => placeV2Claims(result.answer, annotation.claims ?? [], evidenceIndex),
    [annotation.claims, evidenceIndex, result.answer],
  );

  if (result.schema !== V2_SCHEMA) {
    return (
      <Panel tone="amber" title={t("playground.result.unsupportedSchemaTitle")}>
        {t("playground.result.unsupportedSchemaBody")}
      </Panel>
    );
  }

  return (
    <div className="playground-result-body">
      <div className="playground-result-meta">
        <span>{t("playground.result.unpublished")}</span>
        <span>
          {t("playground.result.elapsed", {
            milliseconds: new Intl.NumberFormat(i18n.language).format(result.latency_ms),
          })}
        </span>
      </div>

      <div className="playground-answer">
        <V2AnswerBody
          response={result}
          annotation={annotation}
          marks={placement.marks}
          unplaced={placement.unplaced}
          litPassage={null}
          onPassageClick={() => undefined}
          references={evidenceIndex}
        />
        <V2FootNote annotation={annotation} unplaced={placement.unplaced} />
      </div>

      <section className="playground-sources" aria-labelledby="playground-sources-title">
        <div className="playground-section-head">
          <p className="eyebrow">02</p>
          <h3 id="playground-sources-title">{t("playground.result.sources")}</h3>
        </div>
        {citations.length ? (
          <ol>
            {citations.map((citation, index) => (
              <li key={`${citationKey(citation)}:${index}`}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <p>{describeCitation(citation)}</p>
              </li>
            ))}
          </ol>
        ) : (
          <p className="playground-empty-copy">{t("playground.result.noSources")}</p>
        )}
      </section>
    </div>
  );
}

export function PlaygroundRoute() {
  const { t } = useTranslation();
  const user = useUser();
  const [editorialPrompt, setEditorialPrompt] = useState("");
  const [question, setQuestion] = useState("");
  const [result, setResult] = useState<PlaygroundResponse | null>(null);
  const [errorKey, setErrorKey] = useState<PlaygroundErrorKey | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  if (user.role !== "admin") {
    return (
      <div className="lib">
        <TopBar />
        <main className="playground-shell playground-forbidden">
          <p className="eyebrow">{t("playground.eyebrow")}</p>
          <h1>{t("playground.forbiddenTitle")}</h1>
          <p>{t("playground.forbiddenBody")}</p>
        </main>
      </div>
    );
  }

  const ready = editorialPrompt.trim().length > 0 && question.trim().length > 0;

  async function run(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!ready || isRunning) return;
    setIsRunning(true);
    setErrorKey(null);
    setResult(null);
    try {
      const response = await api.playground({
        editorial_prompt: editorialPrompt,
        query: question,
      });
      setResult(response);
    } catch (error) {
      setErrorKey(playgroundErrorKey(error));
    } finally {
      setIsRunning(false);
    }
  }

  return (
    <div className="lib">
      <TopBar />
      <main className="playground-shell">
        <header className="playground-mast">
          <div>
            <p className="eyebrow">{t("playground.eyebrow")}</p>
            <h1>{t("playground.title")}</h1>
            <p>{t("playground.subtitle")}</p>
          </div>
          <div className="playground-boundary" role="note">
            <span aria-hidden="true">01</span>
            <div>
              <strong>{t("playground.boundaryTitle")}</strong>
              <p>{t("playground.boundaryBody")}</p>
            </div>
          </div>
        </header>

        <div className="playground-grid">
          <form className="playground-form" onSubmit={(event) => void run(event)}>
            <div className="playground-section-head">
              <p className="eyebrow">01</p>
              <h2>{t("playground.form.title")}</h2>
            </div>

            <label htmlFor="editorial-prompt">{t("playground.form.promptLabel")}</label>
            <textarea
              id="editorial-prompt"
              className="input playground-textarea playground-prompt"
              value={editorialPrompt}
              maxLength={2000}
              onChange={(event) => setEditorialPrompt(event.target.value)}
              placeholder={t("playground.form.promptPlaceholder")}
              disabled={isRunning}
            />
            <div className="playground-field-note">
              <span>{t("playground.form.promptHelp")}</span>
              <span>{editorialPrompt.length} / 2.000</span>
            </div>

            <label htmlFor="playground-question">{t("playground.form.questionLabel")}</label>
            <textarea
              id="playground-question"
              className="input playground-textarea playground-question"
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              placeholder={t("playground.form.questionPlaceholder")}
              disabled={isRunning}
            />

            <div className="playground-submit-row">
              <p>{t("playground.form.unpublishedHelp")}</p>
              <Button type="submit" variant="primary" disabled={!ready || isRunning}>
                {isRunning ? t("playground.form.running") : t("playground.form.submit")}
              </Button>
            </div>
          </form>

          <section className="playground-result" aria-live="polite" aria-busy={isRunning}>
            <div className="playground-section-head">
              <p className="eyebrow">02</p>
              <h2>{t("playground.result.title")}</h2>
            </div>
            {isRunning ? (
              <div className="playground-pending">
                <span aria-hidden="true" />
                <div>
                  <strong>{t("playground.result.runningTitle")}</strong>
                  <p>{t("playground.result.runningBody")}</p>
                </div>
              </div>
            ) : errorKey ? (
              <Panel tone="red" title={t("playground.errors.title")}>
                {t(errorKey)}
              </Panel>
            ) : result ? (
              <PlaygroundResult result={result} />
            ) : (
              <div className="playground-empty">
                <span aria-hidden="true">A→</span>
                <h3>{t("playground.result.emptyTitle")}</h3>
                <p>{t("playground.result.emptyBody")}</p>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
